import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import AddParticipantModal from "./AddParticipantModal";
import ReportTable from "../../common/ReportTable/ReportTable";
import ReportTableRowActions from "../../common/ReportTable/ReportTableRowActions";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import DateRangeFilter, { DEFAULT_PRESETS } from "../../common/DateRangeFilter";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import ConfirmModal from "../../common/ConfirmModal";
import {
  fetchAllVolunteers,
  changeVolunteerStatus,
  getVolunteerDisplayName,
} from "./participantService";
import {
  ParticipantAccessGate,
  useParticipantAccess,
} from "./participantAccess";
import {
  ACTIVE_PARTICIPANT_FIELD_OPTIONS,
  DEFAULT_ACTIVE_PARTICIPANT_COLUMNS,
  PM_ACTIONS_COLUMN,
  PM_CONTACT_COLUMN,
  PM_DOB_DATE_RANGE_INITIAL,
  PM_EMAIL_CELL_MAX_LEN,
  PM_NAME_CELL_MAX_LEN,
  PM_PARTICIPANT_COLUMN,
  PM_PINNED_COLUMNS,
  PM_VOL_STATUS_OPTIONS,
  formatParticipantDate,
  getParticipantAgeYears,
  getParticipantProfilePictureUrl,
  getParticipantDbLabel,
  getParticipantDbPillStyle,
  getVolunteerNoCode,
  getVolunteerRowId,
  getVolunteerStatusLabel,
  getVolunteerStatusPillStyle,
  isVolunteerArchived,
  matchesParticipantDobRange,
  matchesParticipantStatusFilter,
  matchTextColumnSearch,
  truncateParticipantText,
  volStatusToCode,
} from "./participantReportUtils";
import ParticipantCodeLabelModal from "./ParticipantCodeLabelModal";
import "./participantmanager.scss";

const PM_STATUS_FILTER_OPTIONS = PM_VOL_STATUS_OPTIONS.map((option) => ({
  value: option.label,
  label: option.label,
}));

const PM_DOB_DATE_PRESETS = DEFAULT_PRESETS;

function placeMenuNearAnchor(anchorEl, { width = 180, height = 200 } = {}) {
  const rect = anchorEl?.getBoundingClientRect?.();
  if (!rect) return { top: 8, left: 8 };
  const gap = 8;
  const pad = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = rect.left;
  let top = rect.bottom + gap;
  if (top + height > vh - pad) {
    const above = rect.top - height - gap;
    top = above >= pad ? above : Math.max(pad, vh - pad - height);
  }
  if (left + width > vw - pad) left = vw - pad - width;
  if (left < pad) left = pad;
  return { top, left };
}

function isUsableProfilePictureUrl(url) {
  const value = String(url ?? "").trim();
  if (!value) return false;
  const lower = value.toLowerCase();
  if (lower.includes("avatar.png") || lower.includes("/images/avatar")) {
    return false;
  }
  return true;
}

function ParticipantAvatarNameCell({ row }) {
  const navigate = useNavigate();
  const volunteerId = getVolunteerRowId(row);
  const name = getVolunteerDisplayName(row);
  const { display: nameDisplay } = truncateParticipantText(
    name,
    PM_NAME_CELL_MAX_LEN,
  );
  const gender = String(row?.Gender ?? "").trim() || "Gender not recorded";
  const photoUrl = getParticipantProfilePictureUrl(row);
  const [imgFailed, setImgFailed] = useState(false);
  const showPhoto = isUsableProfilePictureUrl(photoUrl) && !imgFailed;

  return (
    <button
      type="button"
      className="pm-participant-cell"
      onClick={() => {
        if (volunteerId) navigate(`/participantmanager/participant/${volunteerId}`);
      }}
    >
      {showPhoto ? (
        <img
          src={photoUrl}
          alt=""
          className="pm-participant-cell__avatar"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="pm-participant-cell__avatar pm-participant-cell__avatar--icon" aria-hidden="true">
          <i className="fas fa-user" />
        </span>
      )}
      <span className="pm-participant-cell__text">
        <span className="pm-participant-cell__name" title={name}>
          {nameDisplay}
        </span>
        <span className="pm-participant-cell__meta">{gender}</span>
      </span>
    </button>
  );
}

function ParticipantIdCell({ row, onShowQr }) {
  const idLabel = String(row?.VolunteerNo ?? "").trim() || "—";
  return (
    <div className="pm-id-cell">
      <span className="pm-id-cell__value" title={idLabel}>
        {idLabel}
      </span>
      <button
        type="button"
        className="btn btn-sm btn-link pm-id-cell__qr-btn"
        title="Show barcode"
        aria-label="Show participant barcode"
        onClick={() => onShowQr(row)}
        disabled={!getVolunteerNoCode(row)}
      >
        <i className="fas fa-barcode" aria-hidden="true" />
      </button>
    </div>
  );
}

function ParticipantDobCell({ row }) {
  const dobLabel = formatParticipantDate(row?.DOB);
  const age = getParticipantAgeYears(row?.DOB);
  return (
    <div className="pm-stack-cell">
      <span className="pm-stack-cell__primary">{dobLabel}</span>
      <span className="pm-stack-cell__secondary">
        {age == null ? "—" : `${age} yrs`}
      </span>
    </div>
  );
}

function ParticipantContactCell({ row }) {
  const email = String(row?.EmailId ?? "").trim() || "—";
  const phone = String(row?.ContactNumber ?? "").trim() || "—";
  const { display: emailDisplay } = truncateParticipantText(
    email,
    PM_EMAIL_CELL_MAX_LEN,
  );
  const { display: phoneDisplay } = truncateParticipantText(phone, 18);
  return (
    <div className="pm-stack-cell">
      <span className="pm-stack-cell__primary" title={email}>
        {emailDisplay}
      </span>
      <span className="pm-stack-cell__secondary" title={phone}>
        {phoneDisplay}
      </span>
    </div>
  );
}

function ParticipantStatusCell({ row, onStatusChange }) {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const archived = isVolunteerArchived(row);
  const current = volStatusToCode(row?.VolStatus);
  const label = getVolunteerStatusLabel(row?.VolStatus);
  const style = getVolunteerStatusPillStyle(row?.VolStatus);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (archived) {
    return (
      <span
        className="pm-status-pill pm-status-pill--readonly"
        style={style}
        title={label}
      >
        {label}
      </span>
    );
  }

  return (
    <div className="pm-status-cell" ref={wrapRef}>
      <button
        type="button"
        className="pm-status-pill"
        style={style}
        onClick={(event) => {
          event.stopPropagation();
          setMenuPos(placeMenuNearAnchor(wrapRef.current, { width: 170, height: 180 }));
          setOpen((prev) => !prev);
        }}
        title={`${label} — Change status`}
      >
        <span>{label}</span>
        <i className="fas fa-pencil-alt" aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="pm-status-menu"
              role="listbox"
              aria-label="Change participant status"
              style={{ top: menuPos.top, left: menuPos.left }}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="pm-status-menu__title">Change status</div>
              {PM_VOL_STATUS_OPTIONS.map((option) => {
                const selected = option.value === current;
                const optionStyle = getVolunteerStatusPillStyle(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={[
                      "pm-status-menu__item",
                      selected ? "is-selected" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      setOpen(false);
                      if (!selected) onStatusChange(row, option.value);
                    }}
                  >
                    <span
                      className="pm-status-menu__dot"
                      style={{ background: optionStyle.color }}
                    />
                    {option.label}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

function ParticipantRowActionsMenu({ row, onView, onStatusChange }) {
  const archived = isVolunteerArchived(row);
  const current = volStatusToCode(row?.VolStatus);

  const items = useMemo(() => {
    const menu = [
      {
        label: "View profile",
        icon: "fas fa-user",
        onClick: () => onView(row),
      },
    ];
    if (!archived) {
      if (current !== 0) {
        menu.push({
          label: "Set Active",
          icon: "fas fa-check-circle",
          onClick: () => onStatusChange(row, 0),
        });
      }
      if (current !== 1) {
        menu.push({
          label: "Set Inactive",
          icon: "fas fa-pause-circle",
          onClick: () => onStatusChange(row, 1),
        });
      }
      if (current !== 2) {
        menu.push({
          label: "Remove",
          icon: "fas fa-trash-alt",
          onClick: () => onStatusChange(row, 2),
        });
      }
    }
    return menu;
  }, [archived, current, onStatusChange, onView, row]);

  return <ReportTableRowActions items={items} />;
}

const ParticipantList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const {
    canViewActiveParticipants,
    canViewInactiveParticipants,
    canAddParticipant,
    loading: accessLoading,
  } = useParticipantAccess();
  const canViewParticipants =
    canViewActiveParticipants || canViewInactiveParticipants;

  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [qrRow, setQrRow] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [dobDateRange, setDobDateRange] = useState(PM_DOB_DATE_RANGE_INITIAL);
  const [statusFilterLabels, setStatusFilterLabels] = useState([]);
  const [statusConfirm, setStatusConfirm] = useState({
    open: false,
    row: null,
    nextStatus: null,
  });

  useEffect(() => {
    if (!location.state?.openAddParticipant) return;
    if (canAddParticipant) setAddOpen(true);
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    canAddParticipant,
    location.pathname,
    location.state,
    navigate,
  ]);

  const visibleRows = useMemo(
    () =>
      (rows || []).filter(
        (row) =>
          matchesParticipantDobRange(row, dobDateRange) &&
          matchesParticipantStatusFilter(row, statusFilterLabels),
      ),
    [rows, dobDateRange, statusFilterLabels],
  );

  const loadRows = useCallback(async () => {
    if (!userData?.OrganizationId) {
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const list = await fetchAllVolunteers(userData);
      setRows(Array.isArray(list) ? list : []);
    } catch (err) {
      showAlert({ message: err?.message || "Could not load participants." });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  }, [showAlert, userData]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const applyStatusChange = useCallback(
    async (row, nextStatus) => {
      const volunteerId = getVolunteerRowId(row);
      if (!volunteerId) return;

      showLoader("Updating participant status...");
      try {
        await changeVolunteerStatus(userData, volunteerId, Number(nextStatus));
        await loadRows();
        const label = getVolunteerStatusLabel(nextStatus);
        showAlert({
          message:
            Number(nextStatus) === 1
              ? "Participant set to Inactive. Clinical details are now hidden."
              : Number(nextStatus) === 2
                ? "Participant removed. Clinical details have been deleted."
                : `Participant status updated to ${label}.`,
          type: "success",
        });
      } catch (err) {
        // Status may still have saved (e.g. Remove + app de-link side-effect). Refresh list.
        await loadRows().catch(() => {});
        showAlert({ message: err?.message || "Could not update status." });
      } finally {
        hideLoader();
      }
    },
    [hideLoader, loadRows, showAlert, showLoader, userData],
  );

  const handleStatusChange = useCallback(
    (row, nextStatus) => {
      const volunteerId = getVolunteerRowId(row);
      if (!volunteerId) return;
      const statusCode = Number(nextStatus);
      const currentCode = volStatusToCode(row?.VolStatus);
      if (statusCode === currentCode) return;

      // Confirm Inactive / Remove first. API rejects if Confirmed appointments exist.
      if (statusCode === 1 || statusCode === 2) {
        setStatusConfirm({ open: true, row, nextStatus: statusCode });
        return;
      }

      applyStatusChange(row, statusCode);
    },
    [applyStatusChange],
  );

  const closeStatusConfirm = useCallback(() => {
    setStatusConfirm({ open: false, row: null, nextStatus: null });
  }, []);

  const confirmStatusChange = useCallback(() => {
    const { row, nextStatus } = statusConfirm;
    closeStatusConfirm();
    if (row != null && nextStatus != null) {
      applyStatusChange(row, nextStatus);
    }
  }, [applyStatusChange, closeStatusConfirm, statusConfirm]);

  const statusConfirmCopy = useMemo(() => {
    const row = statusConfirm.row;
    const nextStatus = statusConfirm.nextStatus;
    if (!row || nextStatus == null) {
      return {
        title: "Confirm status change",
        message: "",
        confirmText: "Confirm",
        confirmButtonClass: "btn-primary",
        icon: "fas fa-question-circle text-warning",
      };
    }

    const name = getVolunteerDisplayName(row);
    const id = String(row?.VolunteerNo ?? "").trim();
    const who = id && id !== "—" ? `${name} (${id})` : name;

    if (Number(nextStatus) === 1) {
      return {
        title: "Inactivate participant",
        message: `Are you sure you want to set ${who} as Inactive? Their clinical details will be hidden. You can set them back to Active from this list anytime.`,
        confirmText: "Set Inactive",
        confirmButtonClass: "btn-warning",
        icon: "fas fa-pause-circle text-warning",
      };
    }

    return {
      title: "Remove participant",
      message: `Are you sure you want to remove ${who}? This will delete their clinical details. Only continue if this participant record should no longer be available.`,
      confirmText: "Remove",
      confirmButtonClass: "btn-danger",
      icon: "fas fa-exclamation-triangle text-danger",
    };
  }, [statusConfirm.nextStatus, statusConfirm.row]);

  const handleViewProfile = useCallback(
    (row) => {
      const volunteerId = getVolunteerRowId(row);
      if (!volunteerId) return;
      navigate(`/participantmanager/participant/${volunteerId}`);
    },
    [navigate],
  );

  const renderCell = useCallback(
    (row, field) => {
      if (field === PM_PARTICIPANT_COLUMN) {
        return <ParticipantAvatarNameCell row={row} />;
      }

      if (field === "VolunteerNo") {
        return <ParticipantIdCell row={row} onShowQr={setQrRow} />;
      }

      if (field === "DOB") {
        return <ParticipantDobCell row={row} />;
      }

      if (field === PM_CONTACT_COLUMN) {
        return <ParticipantContactCell row={row} />;
      }

      if (field === "IsUseVolunteerDB") {
        const label = getParticipantDbLabel(row?.IsUseVolunteerDB);
        if (label === "—") return "—";
        return (
          <span
            className="pm-status-pill pm-status-pill--readonly pm-db-pill"
            style={getParticipantDbPillStyle(row?.IsUseVolunteerDB)}
            title={label}
          >
            {label}
          </span>
        );
      }

      if (field === "VolStatus") {
        return (
          <ParticipantStatusCell row={row} onStatusChange={handleStatusChange} />
        );
      }

      if (field === PM_ACTIONS_COLUMN) {
        return (
          <ParticipantRowActionsMenu
            row={row}
            onView={handleViewProfile}
            onStatusChange={handleStatusChange}
          />
        );
      }

      return String(row?.[field] ?? "—");
    },
    [handleStatusChange, handleViewProfile],
  );

  const getColumnHeaderClassName = useCallback((field) => {
    if (field === PM_ACTIONS_COLUMN) return "pm-col-actions";
    if (field === "VolStatus") return "pm-col-status";
    if (field === "DOB") return "pm-col-dob";
    return "";
  }, []);

  const getColumnCellClassName = useCallback((field) => {
    if (field === PM_ACTIONS_COLUMN) return "pm-col-actions";
    if (field === "VolStatus") return "pm-col-status";
    if (field === "DOB") return "pm-col-dob";
    return "";
  }, []);

  const matchColumnSearch = useCallback((row, field, query) => {
    if (
      field === PM_ACTIONS_COLUMN ||
      field === "DOB" ||
      field === "VolStatus"
    ) {
      return true;
    }
    return matchTextColumnSearch(row, field, query);
  }, []);

  const renderColumnSearch = useCallback(
    (field, api) => {
      if (api?.checkOnly) {
        return (
          field === PM_ACTIONS_COLUMN ||
          field === "DOB" ||
          field === "VolStatus"
        );
      }

      if (field === PM_ACTIONS_COLUMN) {
        return <span className="pm-no-filter" aria-hidden="true" />;
      }

      if (field === "DOB") {
        return (
          <DateRangeFilter
            value={dobDateRange}
            onChange={setDobDateRange}
            presetOptions={PM_DOB_DATE_PRESETS}
            placeholder="Date range"
            compact
          />
        );
      }

      if (field === "VolStatus") {
        return (
          <BadgeMultiSelect
            options={PM_STATUS_FILTER_OPTIONS}
            value={statusFilterLabels}
            onChange={setStatusFilterLabels}
            emptyLabel="All status"
            addLabel="+"
            popoverTitle="Filter status"
            popoverSubtitle="Select one or more statuses"
            showSelectAll
            compactSummary
            disabled={api?.disabled}
          />
        );
      }

      return null;
    },
    [dobDateRange, statusFilterLabels],
  );

  return (
    <ParticipantAccessGate
      allow={canViewParticipants}
      loading={accessLoading}
    >
      <div className="participant-manager-page pm-list-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "Participant Manager" },
          ]}
          actions={[
            {
              icon: "fas fa-refresh",
              label: "Refresh",
              onClick: loadRows,
            },
            ...(canAddParticipant
              ? [
                  {
                    icon: "fas fa-plus",
                    label: "Add Participant",
                    onClick: () => setAddOpen(true),
                  },
                ]
              : []),
          ]}
        />

        <div className="pm-report-wrap">
          <ReportTable
            rows={visibleRows}
            loading={listLoading}
            fieldOptions={ACTIVE_PARTICIPANT_FIELD_OPTIONS}
            defaultColumns={DEFAULT_ACTIVE_PARTICIPANT_COLUMNS}
            pinnedRightColumns={PM_PINNED_COLUMNS}
            pinnedRightColumnWidthRem={2.75}
            hideAddColumnFields={PM_PINNED_COLUMNS}
            hideColumnHeaderActionsFields={[
              PM_ACTIONS_COLUMN,
              "VolStatus",
              "DOB",
            ]}
            renderCell={renderCell}
            renderColumnSearch={renderColumnSearch}
            matchColumnSearch={matchColumnSearch}
            getColumnHeaderClassName={getColumnHeaderClassName}
            getColumnCellClassName={getColumnCellClassName}
            persistLayoutKey="pm-participants-v5"
          />
        </div>

        {qrRow ? (
          <ParticipantCodeLabelModal row={qrRow} onClose={() => setQrRow(null)} />
        ) : null}

        <ConfirmModal
          isOpen={statusConfirm.open}
          onClose={closeStatusConfirm}
          onConfirm={confirmStatusChange}
          title={statusConfirmCopy.title}
          message={statusConfirmCopy.message}
          confirmText={statusConfirmCopy.confirmText}
          cancelText="Cancel"
          confirmButtonClass={statusConfirmCopy.confirmButtonClass}
          icon={statusConfirmCopy.icon}
        />

        <AddParticipantModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            loadRows();
          }}
        />

        <InfoAlert {...alertState} onClose={hideAlert} />
      </div>
    </ParticipantAccessGate>
  );
};

export default ParticipantList;
